import { zodResolver } from "@hookform/resolvers/zod";
import { RestartAlt } from "@mui/icons-material";
import {
  Button,
  DialogContent,
  DialogTitle,
  ModalClose,
  ModalDialog,
  Box,
  Typography,
} from "@mui/joy";
import { ApiRoutesTargetConfig } from "griptonite-api-client";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import SteppableNumberInput from "../forms/SteppableNumberInput";
import GenerateTargetsForm from "../Targets/GenerateTargetsForm";
import { useModal } from "./useModal";

const Schema = z.object({
  tolerance: z.number().min(0).max(100),
  warningThreshold: z.number().min(0).max(100),
});
type Schema = z.infer<typeof Schema>;

export default function TargetsSettingsModal({
  climbType,
  config,
}: {
  climbType: "BOULDER" | "SPORT";
  config?: ApiRoutesTargetConfig;
}) {
  const { close } = useModal();
  const [showGenerateTargets, setShowGenerateTargets] = useState(false);

  const methods = useForm<Schema>({
    resolver: zodResolver(Schema),
    defaultValues: {
      tolerance: config?.tolerance ?? 2,
      warningThreshold: config?.warningThreshold ?? 5,
    },
  });

  const onSubmit = async (values: Schema) => {
    // TODO: Implement API call to update config
    console.log("Update config:", values);
    alert("Config update not yet implemented. Coming soon!");
    close();
  };

  if (showGenerateTargets) {
    return (
      <ModalDialog>
        <ModalClose />
        <DialogTitle>Generate new targets</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography level="body-md">
              Let’s set some targets using your current routes as a starting point.
            </Typography>
          </Box>
          <Box sx={{ overflow: "hidden" }}>
            <GenerateTargetsForm
              climbType={climbType}
              onSuccess={() => {
                close();
              }}
            />
          </Box>
        </DialogContent>
      </ModalDialog>
    );
  }

  return (
    <ModalDialog>
      <ModalClose />
      <DialogTitle>Targets settings</DialogTitle>
      <DialogContent sx={{ textAlign: "left" }}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Box mb={3}>
              <SteppableNumberInput name="tolerance" label="Tolerance" min={0} max={100} />
              <Typography level="body-sm" sx={{ mb: 2, mt: 0.5 }} textColor="neutral.600">
                When the number of routes is this far over or under target, they will turn orange.
              </Typography>
              <SteppableNumberInput
                name="warningThreshold"
                label="Warning threshold"
                min={0}
                max={100}
              />
              <Typography level="body-sm" sx={{ mb: 2, mt: 0.5 }} textColor="neutral.600">
                When the number of routes is this far over or under target, they will turn red.
              </Typography>
            </Box>

            <Button
              sx={{ width: "100%", mb: 2 }}
              type="submit"
              loading={methods.formState.isSubmitting}
              loadingPosition="start"
            >
              Save
            </Button>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogContent
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          mx: -2.5,
          px: 2.5,
          pt: 1,
        }}
      >
        <Button
          sx={{ width: "100%", mt: 2 }}
          variant="outlined"
          color="neutral"
          onClick={() => setShowGenerateTargets(true)}
          startDecorator={<RestartAlt />}
        >
          Reset targets or switch mode
        </Button>
      </DialogContent>
    </ModalDialog>
  );
}
